import { useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useServiceRequestTypes } from '../lib/queries';
import { useSubmitServiceRequest } from '../lib/mutations';
import { Button, Input, Select } from '../components/ui';
import { SERVICE_REQUEST_PRIORITY_LABELS } from '../lib/database.types';
import type { ServiceRequestPriority } from '../lib/database.types';

// submit_service_request and the table CHECK constraint both cap the subject
// at 200 characters. Enforced here at input time so the super sees the limit
// rather than hitting a server error.
const SUBJECT_MAX = 200;

const PRIORITY_OPTIONS: ServiceRequestPriority[] = ['low', 'normal', 'urgent', 'emergency'];

const fieldLabel =
    'block font-mono text-[11px] uppercase tracking-widest text-graphite-600 dark:text-graphite-400 mb-1.5';

const textareaClass =
    'w-full rounded border border-graphite-300 dark:border-graphite-700 bg-white dark:bg-graphite-900 ' +
    'px-3 py-2 text-sm text-graphite-900 dark:text-graphite-100 ' +
    'placeholder:text-graphite-400 dark:placeholder:text-graphite-600 ' +
    'focus:outline-none focus:border-graphite-400 dark:focus:border-graphite-600 min-h-[120px] resize-y';

// -----------------------------------------------------------------------------
// Super portal
// -----------------------------------------------------------------------------
// Standalone, mobile-first submission form for superintendents. Supers can
// only submit service requests; they do not see the scheduling board or any
// admin route. A submitted request lands untriaged on the admin dispatch
// board, where an admin links the job and assigns a crew.
//
// submit_service_request takes a subject and description only. There is no
// location column on service_requests, so the builder/community/lot the
// super enters is folded into the top of the description, where the office
// sees it when triaging.

export function SuperPortalPage() {
    const { profile, signOut } = useAuth();
    const { data: requestTypes, isLoading: typesLoading } = useServiceRequestTypes();
    const submit = useSubmitServiceRequest();

    const [typeId, setTypeId] = useState('');
    const [priority, setPriority] = useState<ServiceRequestPriority>('normal');
    const [subject, setSubject] = useState('');
    const [location, setLocation] = useState('');
    const [details, setDetails] = useState('');
    const [formError, setFormError] = useState<string | null>(null);
    const [submitted, setSubmitted] = useState<{ requestNumber: string | null } | null>(null);

    const errorMessage = formError ?? submit.error?.message ?? null;

    function resetForm() {
        setTypeId('');
        setPriority('normal');
        setSubject('');
        setLocation('');
        setDetails('');
        setFormError(null);
        setSubmitted(null);
        submit.reset();
    }

    function handleSubmit() {
        setFormError(null);
        submit.reset();

        if (!typeId) {
            setFormError('Choose a request type.');
            return;
        }
        const trimmedSubject = subject.trim();
        if (!trimmedSubject) {
            setFormError('Add a short subject describing the issue.');
            return;
        }
        const trimmedLocation = location.trim();
        if (!trimmedLocation) {
            setFormError('Add the builder, community and lot so the office can find it.');
            return;
        }

        const trimmedDetails = details.trim();
        const description = trimmedDetails
            ? `Location: ${trimmedLocation}\n\n${trimmedDetails}`
            : `Location: ${trimmedLocation}`;

        submit.mutate(
            {
                p_request_type_id: typeId,
                p_subject: trimmedSubject,
                p_description: description,
                p_priority: priority,
            },
            {
                onSuccess: (row) => {
                    setSubmitted({ requestNumber: row.request_number });
                },
            }
        );
    }

    return (
        <div className="min-h-screen flex flex-col bg-graphite-50 dark:bg-graphite-950">
            {/* Header */}
            <header className="sticky top-0 z-30 bg-graphite-50 dark:bg-graphite-950 border-b border-graphite-200 dark:border-graphite-800">
                <div className="max-w-xl mx-auto flex items-center h-14 px-4 gap-4">
                    <div className="flex items-baseline gap-2.5 min-w-0">
                        <span className="font-display font-bold text-graphite-900 dark:text-graphite-50 text-lg tracking-tight">
                            PPC
                        </span>
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-graphite-500">
                            Service Requests
                        </span>
                    </div>
                    <div className="flex-1" />
                    {profile?.full_name ? (
                        <span className="hidden sm:inline text-sm text-graphite-600 dark:text-graphite-400">
                            {profile.full_name}
                        </span>
                    ) : null}
                    <button
                        type="button"
                        onClick={() => {
                            void signOut();
                        }}
                        className="font-mono text-xs uppercase tracking-widest text-graphite-600 hover:text-graphite-900 dark:text-graphite-400 dark:hover:text-graphite-100 transition-colors"
                    >
                        Sign out
                    </button>
                </div>
            </header>

            <main className="flex-1 w-full max-w-xl mx-auto px-4 py-6">
                {submitted ? (
                    /* Confirmation */
                    <div className="border border-done-500/30 bg-done-500/[0.06] rounded px-5 py-7 text-center">
                        <div className="font-display font-bold text-xl text-graphite-900 dark:text-graphite-50 mb-1">
                            Request submitted
                        </div>
                        <p className="text-sm text-graphite-600 dark:text-graphite-400">
                            The PPC office has it and will dispatch a crew.
                        </p>
                        {submitted.requestNumber ? (
                            <p className="mt-2 font-mono text-sm text-graphite-500">
                                Reference {submitted.requestNumber}
                            </p>
                        ) : null}
                        <div className="mt-6">
                            <Button variant="primary" size="sm" onClick={resetForm}>
                                Submit another request
                            </Button>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Intro */}
                        <div className="mb-5">
                            <h1 className="font-display font-bold text-2xl text-graphite-900 dark:text-graphite-50">
                                Submit a request
                            </h1>
                            <p className="text-sm text-graphite-600 dark:text-graphite-400 mt-1">
                                Send a callback, punch list item, warranty claim or service
                                request to the PPC office. They will review it and dispatch a
                                crew.
                            </p>
                        </div>

                        {typesLoading ? (
                            <div className="font-mono text-xs text-graphite-500 uppercase tracking-widest">
                                Loading...
                            </div>
                        ) : !requestTypes || requestTypes.length === 0 ? (
                            <div className="border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 px-4 py-3 rounded text-sm">
                                Request types could not be loaded. Refresh the page, and if it
                                keeps happening contact the PPC office.
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {/* Request type */}
                                <label className="block">
                                    <span className={fieldLabel}>Request type</span>
                                    <Select
                                        id="sr-type"
                                        value={typeId}
                                        onChange={(e) => setTypeId(e.target.value)}
                                    >
                                        <option value="">Choose a type</option>
                                        {requestTypes.map((t) => (
                                            <option key={t.id} value={t.id}>
                                                {t.label}
                                            </option>
                                        ))}
                                    </Select>
                                </label>

                                {/* Priority */}
                                <label className="block">
                                    <span className={fieldLabel}>Priority</span>
                                    <Select
                                        id="sr-priority"
                                        value={priority}
                                        onChange={(e) =>
                                            setPriority(e.target.value as ServiceRequestPriority)
                                        }
                                    >
                                        {PRIORITY_OPTIONS.map((p) => (
                                            <option key={p} value={p}>
                                                {SERVICE_REQUEST_PRIORITY_LABELS[p]}
                                            </option>
                                        ))}
                                    </Select>
                                </label>

                                {/* Subject */}
                                <div>
                                    <label className="block">
                                        <span className={fieldLabel}>Subject</span>
                                        <Input
                                            id="sr-subject"
                                            placeholder="Short summary, e.g. Cracked driveway"
                                            value={subject}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                if (v.length <= SUBJECT_MAX) setSubject(v);
                                            }}
                                        />
                                    </label>
                                    <div className="mt-1 text-right font-mono text-[10px] text-graphite-400">
                                        {subject.length}/{SUBJECT_MAX}
                                    </div>
                                </div>

                                {/* Location */}
                                <label className="block">
                                    <span className={fieldLabel}>Builder, community and lot</span>
                                    <Input
                                        id="sr-location"
                                        placeholder="e.g. Red Cedar, Holly Grove, Lot 12"
                                        value={location}
                                        onChange={(e) => setLocation(e.target.value)}
                                    />
                                </label>

                                {/* Details */}
                                <label className="block">
                                    <span className={fieldLabel}>Details</span>
                                    <textarea
                                        id="sr-details"
                                        className={textareaClass}
                                        placeholder="What needs attention? Anything the crew should know before they arrive."
                                        value={details}
                                        onChange={(e) => setDetails(e.target.value)}
                                    />
                                </label>

                                {errorMessage ? (
                                    <div className="border border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400 px-4 py-3 rounded font-mono text-sm">
                                        {errorMessage}
                                    </div>
                                ) : null}

                                <div className="pt-1">
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={handleSubmit}
                                        disabled={submit.isPending}
                                    >
                                        {submit.isPending ? 'Submitting...' : 'Submit request'}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </main>
        </div>
    );
}
